import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

// 배경색 대비에 따라 진한/밝은 글자색 선택 (YIQ 근사). "#RRGGBB" 가정.
function contrastColor(bg) {
  if (typeof bg !== "string" || bg[0] !== "#" || bg.length < 7) return "#111827"
  const r = parseInt(bg.slice(1, 3), 16)
  const g = parseInt(bg.slice(3, 5), 16)
  const b = parseInt(bg.slice(5, 7), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 150 ? "#111827" : "#ffffff"
}

// 각 항목(막대/도넛 조각)에 값을 그려주는 인라인 플러그인.
//   - 일반(비스택) 막대: 막대 위에 진한 글자
//   - 스택 막대 세그먼트 / 도넛 조각: 중앙에 배경 대비 글자
//   - 값이 0/null 인 항목은 표시하지 않음
const valueLabelsPlugin = {
  id: "valueLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart
    const isBar = chart.config.type === "bar"
    const stacked = isBar && !!(chart.options.scales?.y?.stacked)
    ctx.save()
    ctx.font = "bold 12px sans-serif"
    ctx.textAlign = "center"
    chart.data.datasets.forEach((dataset, di) => {
      const meta = chart.getDatasetMeta(di)
      if (meta.hidden) return
      meta.data.forEach((element, index) => {
        const value = dataset.data[index]
        if (!value) return
        // 비스택 막대에서 너무 낮은 막대만 예외적으로 막대 위에 진한 글자로 표시.
        if (isBar && !stacked && Math.abs(element.base - element.y) < 18) {
          ctx.textBaseline = "bottom"
          ctx.fillStyle = "#111827"
          ctx.fillText(String(value), element.x, element.y - 5)
          return
        }
        // 그 외(막대/스택 세그먼트/도넛 조각)는 도형의 기하학적 정중앙에 표시.
        // 막대는 tooltipPosition() 이 상단을 반환하므로 getCenterPoint() 를 써야 한다.
        const p = element.getCenterPoint()
        ctx.textBaseline = "middle"
        ctx.fillStyle = contrastColor(element.options?.backgroundColor)
        ctx.fillText(String(value), p.x, p.y)
      })
    })
    ctx.restore()
  }
}

// 범용 차트 렌더러 (Chart.js) — canvas 에 부착.
//   data-controller="chart"
//   data-chart-type-value="bar|doughnut"
//   data-chart-data-value=(Chart.js data JSON)
//   data-chart-options-value=(Chart.js options JSON, 선택)
//   data-chart-width-value / data-chart-height-value=(고정 크기 px)
//
// 크기는 고정(responsive: false)이 기본 — 반응형 모드는 컨테이너 측정 타이밍에
// 의존해서 Turbo 복원(뒤로가기) 시 잘못된 크기로 렌더되는 문제가 있었다
// (호버 시 리사이즈 체크에서야 복구되는 증상). 고정 크기는 측정 자체가 없어
// 타이밍 문제가 구조적으로 발생하지 않는다. width/height 미지정 시에만 반응형.
export default class extends Controller {
  static values = {
    type: String,
    data: Object,
    options: { type: Object, default: {} },
    width: Number,
    height: Number
  }

  connect() {
    this.beforeCache = () => this.teardown()
    document.addEventListener("turbo:before-cache", this.beforeCache)
    this.render()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.teardown()
  }

  render() {
    // 캐시 복원 등으로 남은 잔여 크기 속성 제거 (dpr 스케일 중복 방지)
    this.element.removeAttribute("width")
    this.element.removeAttribute("height")
    this.element.style.removeProperty("width")
    this.element.style.removeProperty("height")

    const fixedSize = this.hasWidthValue && this.hasHeightValue
    if (fixedSize) {
      this.element.width = this.widthValue
      this.element.height = this.heightValue
    }

    const options = {
      responsive: !fixedSize,
      maintainAspectRatio: false,
      ...this.optionsValue
    }

    // 막대 그래프의 맨 위(최댓값) 눈금 라벨은 캔버스 상단에서 잘리므로 숨긴다.
    // 옵션은 JSON 으로 전달돼 함수 콜백을 담을 수 없어 여기서 주입한다.
    if (this.typeValue === "bar") {
      const y = (options.scales = options.scales || {}).y = options.scales.y || {}
      const ticks = y.ticks = y.ticks || {}
      ticks.callback = function (value, index, allTicks) {
        const max = Math.max(...allTicks.map((t) => t.value))
        return value === max ? "" : value
      }
    }

    this.chart = new Chart(this.element, {
      type: this.typeValue,
      data: this.dataValue,
      options,
      plugins: [valueLabelsPlugin]
    })
  }

  teardown() {
    this.chart?.destroy()
    this.chart = null
  }
}
